import { CreditCardIcon, GiftIcon, PauseIcon, PlayIcon, RepeatIcon, SkipForwardIcon, XIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { DeleteButton } from '../ui/DeleteButton';
import { useAdmin } from '../AdminContext';
import { btnIcon, btnSecondary, inputCls } from '../ui/styles';
import { deliveriesUntilGift } from '../../utils/account';
import { subscriptionCadences, subscriptionRefill } from '../../utils/refill';
import { dayLong, dayShort } from '../../core/format/dates';
import { euro } from '../../core/format/currency';
import { POSTPONE_DAYS } from '../../core/rules/subscription';
import type { SubscriptionStatus } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';
const statusBadge: Record<SubscriptionStatus, {
  tone: 'success' | 'soft' | 'neutral';
  label: string;
}> = {
  actief: {
    tone: 'success',
    label: 'Actief'
  },
  gepauzeerd: {
    tone: 'soft',
    label: 'Gepauzeerd'
  },
  gestopt: {
    tone: 'neutral',
    label: 'Gestopt'
  }
};

/**
 * Het Bewust Abonnement van één klant — dezelfde acties als in Mijn omgeving,
 * zodat Sarah het aan de telefoon of in de salon voor haar kan regelen.
 */
export function CustomerSubscription({
  ctl
}: {
  ctl: CustomerFileControl;
}) {
  const {
    index,
    site
  } = useAdmin();
  const {
    account
  } = ctl.file;
  const sub = account.subscription;
  const name = account.customer.firstName;
  if (!sub) {
    return <EmptyState icon={RepeatIcon} title={`${name} heeft geen abonnement`} text="Een abonnement start de klant zelf, in de winkelmand of in Mijn omgeving. Zo blijft het haar keuze." data-id="element-1336" data-mp="31s2svio52a5j" />;
  }
  const act = ctl.subscription;
  const active = sub.status === 'actief';
  const policySub = site.policy.subscription;
  // Slim bijvullen: per doos alleen wat op raakt. Zo ziet Sarah wat ze straks inpakt.
  const refill = subscriptionRefill(sub, index.productById, policySub, 3);
  const nextBox = refill.boxes[0];
  const cadences = subscriptionCadences(sub, index.productById, policySub);
  const untilGift = deliveriesUntilGift(sub);
  const lastItem = sub.items.length <= 1;
  return <div className="grid gap-6 lg:grid-cols-3" data-id="element-1337" data-mp="39bc1vasqg6u2">
      <div className="min-w-0 space-y-6 lg:col-span-2" data-id="element-1338" data-mp="35bjmof8u32fq">
        <Panel title="Volgende levering" actions={<Badge tone={statusBadge[sub.status].tone} data-id="element-1340" data-mp="30bxdcgw4m6c7">{statusBadge[sub.status].label}</Badge>} data-id="element-1339" data-mp="35b1jszro1pyu">
          <p className="font-display text-[26px] font-light capitalize leading-tight text-ink" data-id="element-1341" data-mp="2vddnw6ibqcf3">
            {sub.status === 'gestopt' ? 'Geen leveringen meer' : dayLong(sub.nextDelivery)}
          </p>
          {sub.paymentIssue && sub.status === 'actief' && <p className="mt-2 flex items-start gap-1.5 text-[13px] text-clay" data-id="element-1342" data-mp="2me8o6wv10d1c">
              <CreditCardIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-1343" data-mp="olcy7diq0jx9" />
              <span data-id="element-1344" data-mp="klkj0hytnfix">
                {sub.paymentIssue.reason === 'verlopen' ? 'De betaling voor deze levering is verlopen.' : 'De betaling voor deze levering lukte niet.'} {name} ziet
                in Mijn omgeving een knop om opnieuw te betalen. Niet inpakken tot hij binnen is (order {sub.paymentIssue.orderId}).
              </span>
            </p>}
          {sub.status === 'gepauzeerd' && sub.pausedUntil && <p className="mt-1 text-[13px] text-muted" data-id="element-1345" data-mp="2gbx5vdb48cpg">Gepauzeerd tot en met {dayLong(sub.pausedUntil)}.</p>}
          {active && <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted" data-id="element-1346" data-mp="1ojy05z869vjm">
              <GiftIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-1347" data-mp="1sk030bpn8v0h" />
              {untilGift === 1 ? `In deze doos: ${policySub.giftDescription}` : `Cadeau over ${untilGift} dozen`} · {sub.deliveriesDone} verstuurd
            </p>}

          <div className="mt-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-id="element-1348" data-mp="34218pighc8fe">
            {active && <>
                <button type="button" onClick={() => act({
              type: 'skip'
            }, 'Levering overgeslagen')} className={btnSecondary} data-id="element-1349" data-mp="37h2pv4jbk91h">
                  <SkipForwardIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1350" data-mp="32jt1ls7o23f8" />
                  Sla over
                </button>
                <button type="button" onClick={() => act({
              type: 'postpone',
              days: POSTPONE_DAYS
            }, 'Een week opgeschoven')} className={btnSecondary} data-id="element-1351" data-mp="3cgp1j9ji1h86">
                  Week later
                </button>
                {site.policy.subscription.pauseOptions.map(o => <button key={o.weeks} type="button" onClick={() => act({
              type: 'pause',
              weeks: o.weeks
            }, `Gepauzeerd voor ${o.label}`)} aria-label={`Pauzeer ${o.label}`} className={btnSecondary} data-id="element-1352" data-mp="3lh3r4l3hxihh">
                    <PauseIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1353" data-mp="cz7hvegmnzs0" />
                    {o.label}
                  </button>)}
              </>}
            {!active && <button type="button" onClick={() => act({
            type: 'resume'
          }, 'Abonnement loopt weer')} className={btnSecondary} data-id="element-1354" data-mp="wn3tfrk21yav">
                <PlayIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1355" data-mp="imj9gs2elwfs" />
                Hervatten
              </button>}
          </div>
        </Panel>

        <Panel title="Om bij te vullen" description={nextBox && nextBox.lines.length ? `Per doos alleen wat op raakt. Volgende doos ${euro(nextBox.total, {
        cents: true
      })}, altijd zonder verzendkosten.` : 'Per doos alleen wat op raakt. De eerstvolgende doos is nog leeg: niets raakt op.'} flush data-id="element-1356" data-mp="8x9dv28cpvmx">
          <ul className="divide-y divide-line" data-id="element-1357" data-mp="3m4jkyh764jy5">
            {sub.items.map(item => {
            const p = index.productById(item.productId);
            const productName = p?.name ?? item.productId;
            const plan = refill.items.find(i => i.productId === item.productId);
            const unknown = refill.unknown.includes(item.productId);
            return <li key={item.productId} className="flex items-center gap-3 px-4 py-3 sm:px-5" data-id="element-1358" data-mp="1mxcjzztte2in">
                  {p ? <ProductImage src={p.image} className="h-10 w-10 shrink-0 rounded p-0.5" data-id="element-1359" data-mp="1iwaqlzmkwkol" /> : <span className="h-10 w-10 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-1360" data-mp="2fkyttptmy3dy" />}
                  <span className="min-w-0 flex-1" data-id="element-1361" data-mp="2angmu4z6hy8l">
                    <span className="block truncate text-[14px] text-ink" data-id="element-1362" data-mp="26l3hvis9a4cv">{productName}</span>
                    {p && <span className="block text-[12px] text-muted" data-id="element-1363" data-mp="21l7cpg7to6vq">
                        {euro(p.price)}
                        {plan && !unknown && ` · gaat ± ${plan.lastsWeeks} weken mee · op ± ${dayShort(plan.runsOutOn)}`}
                      </span>}
                    {unknown && <span className="block text-[12px] text-clay" data-id="element-1364" data-mp="31w0b59zlwdup">Geen “gaat ± … mee” in de catalogus: gaat elke doos mee. Vul het aan bij Aanbod › Producten.</span>}
                  </span>
                  <button type="button" onClick={() => act({
                type: 'removeItem',
                productId: item.productId
              })} disabled={lastItem} title={lastItem ? 'Het laatste product blijft staan: pauzeer of stop in plaats daarvan' : undefined} aria-label={`${p?.name ?? 'Product'} uit het abonnement halen`} className={btnIcon} data-id="element-1365" data-mp="3atu6oipjr3gt">
                    <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1366" data-mp="2flidmu24yug" />
                  </button>
                </li>;
          })}
          </ul>
          {sub.extras.length > 0 && <p className="border-t border-line px-4 py-3 text-[13px] text-muted sm:px-5" data-id="element-1367" data-mp="247q9wx7n9u4n">
              Eenmalig erbij: <span className="text-ink" data-id="element-1368" data-mp="1l954dnqrp7mw">{sub.extras.map(id => index.productById(id)?.name ?? id).join(', ')}</span>
            </p>}
        </Panel>
      </div>

      <div className="min-w-0 space-y-6" data-id="element-1369" data-mp="1u39v875wij2f">
        <Panel title="Ritme" data-id="element-1370" data-mp="1u1ynxxd8yt6k">
          <select value={sub.intervalWeeks} onChange={e => act({
          type: 'setInterval',
          weeks: Number(e.target.value)
        }, 'Ritme aangepast')} disabled={sub.status === 'gestopt'} aria-label="Elke hoeveel weken" className={inputCls} data-id="element-1371" data-mp="1q14z91rzdacm">
            {cadences.map(w => <option key={w} value={w} data-id="element-1372" data-mp="1bdbj9fyzg5ti">
                Elke {w} weken
              </option>)}
          </select>
          <p className="mt-1.5 text-[12px] text-muted" data-id="element-1373" data-mp="16bukbq026tfk">Alleen ritmes waarbij elke doos minstens {euro(policySub.minBoxValue)} is.</p>
          {active && <ol className="mt-4 space-y-2.5 text-[14px] text-ink" aria-label="Komende dozen" data-id="element-1374" data-mp="2ht44ker0hphx">
              {refill.boxes.map((b, i) => <li key={b.date} data-id="element-1375" data-mp="2q81sg7txnvn9" data-mpi={i}>
                  <span className="flex justify-between gap-3" data-id="element-1376" data-mp="2uaonehlwhwoo">
                    <span data-id="element-1377" data-mp="2uc9j4ajyblo4">{dayShort(b.date)}</span>
                    <span className="tabular-nums text-muted" data-id="element-1378" data-mp="2gbp1vitopxwq">
                      {b.lines.length ? euro(b.total) : 'geen doos'}
                      {i + 1 === untilGift && <span className="ml-2 text-[12px] text-clay" data-id="element-1379" data-mp="2lccq5wqj40n9">met cadeau</span>}
                    </span>
                  </span>
                  {b.lines.length > 0 && <span className="block text-[12px] text-muted" data-id="element-1380" data-mp="3dye9q9b2j4qi">
                      {b.lines.map(l => `${l.qty > 1 ? `${l.qty}× ` : ''}${index.productById(l.productId)?.name ?? l.productId}`).join(', ')}
                    </span>}
                </li>)}
            </ol>}
        </Panel>
        {sub.status !== 'gestopt' && <DeleteButton label="Abonnement stoppen" warning={`Alleen op verzoek van ${name}`} onConfirm={() => act({
        type: 'cancel',
        reason: {
          code: 'door-salon'
        }
      }, 'Abonnement gestopt')} data-id="element-1381" data-mp="l2pqjlho68u" />}
      </div>
    </div>;
}